/**
 * Regla única de derivación del nombre visible de la cuenta.
 *
 * **Réplica** de `AccountIdentity.normalizeName` de `quizoteca-student`
 * (`lib/features/auth/domain/account_identity.dart`, 2026-11-01), adoptada en el
 * portal el 2026-11-02 por decisión del owner: todas las apps del ecosistema
 * derivan el nombre igual, en el registro, en el primer ingreso con Google y en
 * la edición del perfil. Declarada en `specs/MODELO-DATOS.md` §USER
 * `display_name`.
 *
 * 1. El nombre recortado; vacío cuenta como ausente.
 * 2. Si no, la parte local del correo (antes de la `@`), recortada; vacía
 *    cuenta como ausente.
 * 3. Si no, {@link DEFAULT_DISPLAY_NAME}.
 * 4. El resultado se trunca a {@link DISPLAY_NAME_MAX_LENGTH} unidades UTF-16
 *    sin partir una pareja de sustitutos.
 *
 * Así el valor cumple siempre la regla RTDB de la presencia del aula
 * (`students/{uid}.name`, `length ∈ [1, 100]`) y nunca queda vacío. El correo
 * no se toca. `String.prototype.length` mide en unidades UTF-16, igual que el
 * `String.length` de Dart: la semántica es idéntica.
 */

/** Largo máximo del nombre, en unidades UTF-16 (regla RTDB de la presencia). */
export const DISPLAY_NAME_MAX_LENGTH = 100

/** Nombre cuando la cuenta no tiene nombre ni correo utilizables. */
export const DEFAULT_DISPLAY_NAME = 'Estudiante'

const presentOrNull = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

/** `true` si el nombre está ausente o sólo tiene espacios. */
export const isBlankName = (value: string | null | undefined): boolean =>
  presentOrNull(value) === null

const truncateUtf16 = (value: string): string => {
  if (value.length <= DISPLAY_NAME_MAX_LENGTH) return value
  let end = DISPLAY_NAME_MAX_LENGTH
  const last = value.charCodeAt(end - 1)
  if (last >= 0xd800 && last <= 0xdbff) end -= 1
  return value.substring(0, end)
}

export const deriveDisplayName = (
  displayName: string | null | undefined,
  email: string | null | undefined
): string => {
  const fromDisplayName = presentOrNull(displayName)
  const fromEmail = email == null ? null : presentOrNull(email.split('@')[0])
  return truncateUtf16(fromDisplayName ?? fromEmail ?? DEFAULT_DISPLAY_NAME)
}
